import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Calculator,
  Info,
  BookOpen,
  Lightbulb,
  Copy,
  Check,
  RotateCcw,
  TrendingUp,
  Eye,
  EyeOff,
  Download,
  Share2,
  Sparkles,
  Zap,
  CheckCircle2,
  XCircle,
  LineChart,
  ArrowRight,
  Hash,
  Maximize2,
  Minimize2,
  Grid3x3
} from 'lucide-react';
import "@/App.css";

// Function analysis with detailed steps
const analyzeFunction = (expression, variable, domain = { min: -10, max: 10 }) => {
  const steps = [];
  let current = expression.trim();
  
  steps.push(`Analyzing function: f(${variable}) = ${current}`);
  
  // Parse polynomial for simple cases
  const parsePolynomial = (expr) => {
    const termRegex = /([+-]?\d*\.?\d*)\*?([a-z])\^?(\d+)?/gi;
    const constantRegex = /^([+-]?\d+\.?\d*)$/;
    
    const terms = [];
    let match;
    expr = expr.replace(/\s/g, '');
    
    while ((match = termRegex.exec(expr)) !== null) {
      const coef = match[1] === '' || match[1] === '+' ? 1 : match[1] === '-' ? -1 : parseFloat(match[1]);
      const variable = match[2];
      const power = match[3] ? parseInt(match[3]) : 1;
      terms.push({ coef, variable, power });
    }
    
    const constMatch = expr.match(constantRegex);
    if (constMatch && terms.length === 0) {
      terms.push({ coef: parseFloat(constMatch[1]), variable: null, power: 0 });
    }
    
    return terms;
  };
  
  const terms = parsePolynomial(current);
  
  // Determine function type
  let functionType = 'polynomial';
  let degree = 0;
  let leadingCoef = 0;
  
  terms.forEach(term => {
    if (term.variable && term.power > degree) {
      degree = term.power;
      leadingCoef = term.coef;
    }
  });
  
  if (degree === 0) functionType = 'constant';
  else if (degree === 1) functionType = 'linear';
  else if (degree === 2) functionType = 'quadratic';
  else if (degree === 3) functionType = 'cubic';
  else functionType = `polynomial (degree ${degree})`;
  
  steps.push(`\nFunction Type: ${functionType}`);
  if (degree > 0) {
    steps.push(`Leading term: ${leadingCoef}${variable}^${degree}`);
  }
  
  // Calculate domain (for polynomials, all real numbers)
  const analyzedDomain = {
    description: "All real numbers (-∞, ∞)",
    intervals: [{ start: -Infinity, end: Infinity, notation: "(-∞, ∞)" }]
  };
  steps.push(`\nDomain: ${analyzedDomain.description}`);
  
  // Calculate range
  let analyzedRange = {};
  if (degree === 0) {
    // Constant function
    const constValue = terms[0]?.coef || 0;
    analyzedRange = {
      description: `{${constValue}}`,
      min: constValue,
      max: constValue
    };
    steps.push(`Range: {${constValue}} (constant function)`);
  } else if (degree === 1) {
    // Linear function
    analyzedRange = {
      description: "All real numbers (-∞, ∞)",
      min: -Infinity,
      max: Infinity
    };
    steps.push(`Range: All real numbers (linear function)`);
  } else if (degree === 2) {
    // Quadratic - find vertex
    const a = terms.find(t => t.power === 2)?.coef || 0;
    const b = terms.find(t => t.power === 1)?.coef || 0;
    const c = terms.find(t => t.power === 0)?.coef || 0;
    
    const vertexX = -b / (2 * a);
    const vertexY = a * vertexX * vertexX + b * vertexX + c;
    
    if (a > 0) {
      analyzedRange = {
        description: `[${vertexY.toFixed(4)}, ∞)`,
        min: vertexY,
        max: Infinity
      };
      steps.push(`Range: [${vertexY.toFixed(4)}, ∞) (opens upward, vertex at minimum)`);
    } else {
      analyzedRange = {
        description: `(-∞, ${vertexY.toFixed(4)}]`,
        min: -Infinity,
        max: vertexY
      };
      steps.push(`Range: (-∞, ${vertexY.toFixed(4)}] (opens downward, vertex at maximum)`);
    }
  } else {
    // Higher degree polynomial
    analyzedRange = {
      description: "Depends on leading coefficient and degree",
      min: leadingCoef > 0 && degree % 2 === 0 ? "Has minimum" : -Infinity,
      max: leadingCoef < 0 && degree % 2 === 0 ? "Has maximum" : Infinity
    };
    steps.push(`Range: For odd degree, (-∞, ∞); for even degree, depends on extrema`);
  }
  
  // Find intercepts
  const intercepts = {
    y: null,
    x: []
  };
  
  // Y-intercept (x=0)
  let yIntercept = 0;
  terms.forEach(term => {
    if (term.variable === null || term.power === 0) {
      yIntercept = term.coef;
    }
  });
  intercepts.y = yIntercept;
  steps.push(`\nY-intercept: (0, ${yIntercept.toFixed(4)})`);
  
  // X-intercepts (solve f(x) = 0)
  if (degree === 1) {
    const a = terms.find(t => t.power === 1)?.coef || 1;
    const b = terms.find(t => t.power === 0)?.coef || 0;
    const xInt = -b / a;
    intercepts.x.push(xInt);
    steps.push(`X-intercept: (${xInt.toFixed(4)}, 0)`);
  } else if (degree === 2) {
    const a = terms.find(t => t.power === 2)?.coef || 1;
    const b = terms.find(t => t.power === 1)?.coef || 0;
    const c = terms.find(t => t.power === 0)?.coef || 0;
    
    const discriminant = b * b - 4 * a * c;
    if (discriminant > 0) {
      const x1 = (-b + Math.sqrt(discriminant)) / (2 * a);
      const x2 = (-b - Math.sqrt(discriminant)) / (2 * a);
      intercepts.x.push(x1, x2);
      steps.push(`X-intercepts: (${x1.toFixed(4)}, 0) and (${x2.toFixed(4)}, 0)`);
      steps.push(`  • Using quadratic formula with discriminant = ${discriminant.toFixed(4)}`);
    } else if (discriminant === 0) {
      const x1 = -b / (2 * a);
      intercepts.x.push(x1);
      steps.push(`X-intercept: (${x1.toFixed(4)}, 0) (double root)`);
    } else {
      steps.push(`No real x-intercepts (discriminant = ${discriminant.toFixed(4)} < 0)`);
    }
  } else {
    steps.push(`X-intercepts: Require numerical methods for degree ${degree}`);
  }
  
  // Critical points (where derivative = 0)
  const criticalPoints = [];
  if (degree > 1) {
    steps.push(`\nFinding critical points (f'(${variable}) = 0):`);
    
    // Take derivative
    const derivative = [];
    terms.forEach(term => {
      if (term.variable && term.power > 0) {
        derivative.push({
          coef: term.coef * term.power,
          variable: term.variable,
          power: term.power - 1
        });
      }
    });
    
    let derivStr = '';
    derivative.forEach((term, idx) => {
      if (term.power === 0) {
        derivStr += (idx === 0 ? '' : term.coef >= 0 ? ' + ' : ' - ') + Math.abs(term.coef);
      } else if (term.power === 1) {
        derivStr += (idx === 0 ? (term.coef < 0 ? '-' : '') : term.coef >= 0 ? ' + ' : ' - ') + Math.abs(term.coef) + term.variable;
      } else {
        derivStr += (idx === 0 ? (term.coef < 0 ? '-' : '') : term.coef >= 0 ? ' + ' : ' - ') + Math.abs(term.coef) + term.variable + '^' + term.power;
      }
    });
    steps.push(`  • f'(${variable}) = ${derivStr}`);
    
    // Solve derivative = 0 for quadratic
    if (degree === 2) {
      const a = derivative.find(t => t.power === 1)?.coef || 0;
      const b = derivative.find(t => t.power === 0)?.coef || 0;
      if (a !== 0) {
        const critX = -b / a;
        // Evaluate original function at critical point
        let critY = 0;
        terms.forEach(term => {
          if (term.variable === null || term.power === 0) {
            critY += term.coef;
          } else {
            critY += term.coef * Math.pow(critX, term.power);
          }
        });
        criticalPoints.push({ x: critX, y: critY });
        steps.push(`  • Critical point: (${critX.toFixed(4)}, ${critY.toFixed(4)})`);
        
        // Determine if max or min
        const a2 = terms.find(t => t.power === 2)?.coef || 0;
        if (a2 > 0) {
          steps.push(`  • This is a minimum (a = ${a2} > 0)`);
        } else {
          steps.push(`  • This is a maximum (a = ${a2} < 0)`);
        }
      }
    } else if (degree === 3) {
      // Cubic: use quadratic formula on derivative
      const a = derivative.find(t => t.power === 2)?.coef || 0;
      const b = derivative.find(t => t.power === 1)?.coef || 0;
      const c = derivative.find(t => t.power === 0)?.coef || 0;
      
      if (a !== 0) {
        const discriminant = b * b - 4 * a * c;
        if (discriminant >= 0) {
          const x1 = (-b + Math.sqrt(discriminant)) / (2 * a);
          const x2 = (-b - Math.sqrt(discriminant)) / (2 * a);
          
          // Evaluate at both points
          [x1, x2].forEach(xVal => {
            let yVal = 0;
            terms.forEach(term => {
              if (term.variable === null || term.power === 0) {
                yVal += term.coef;
              } else {
                yVal += term.coef * Math.pow(xVal, term.power);
              }
            });
            criticalPoints.push({ x: xVal, y: yVal });
            steps.push(`  • Critical point: (${xVal.toFixed(4)}, ${yVal.toFixed(4)})`);
          });
        }
      }
    } else {
      steps.push(`  • Use numerical methods to find critical points for degree ${degree}`);
    }
  }
  
  // Inflection points (where second derivative = 0)
  if (degree >= 2) {
    steps.push(`\nInflection points (f''(${variable}) = 0):`);
    
    // Take second derivative
    const secondDerivative = [];
    terms.forEach(term => {
      if (term.variable && term.power > 1) {
        secondDerivative.push({
          coef: term.coef * term.power * (term.power - 1),
          variable: term.variable,
          power: term.power - 2
        });
      }
    });
    
    if (degree === 2) {
      const a = terms.find(t => t.power === 2)?.coef || 0;
      steps.push(`  • f''(${variable}) = ${2 * a} (constant, no inflection points)`);
    } else if (degree === 3) {
      const a = secondDerivative.find(t => t.power === 1)?.coef || 0;
      const b = secondDerivative.find(t => t.power === 0)?.coef || 0;
      if (a !== 0) {
        const inflX = -b / a;
        let inflY = 0;
        terms.forEach(term => {
          if (term.variable === null || term.power === 0) {
            inflY += term.coef;
          } else {
            inflY += term.coef * Math.pow(inflX, term.power);
          }
        });
        steps.push(`  • Inflection point: (${inflX.toFixed(4)}, ${inflY.toFixed(4)})`);
      }
    } else {
      steps.push(`  • Solve f''(${variable}) = 0 for inflection points`);
    }
  }
  
  // End behavior
  steps.push(`\nEnd Behavior:`);
  if (degree === 0) {
    const constValue = terms[0]?.coef || 0;
    steps.push(`  • As ${variable}→±∞: f(${variable})→${constValue} (horizontal line)`);
  } else if (degree > 0) {
    if (degree % 2 === 1) {
      // Odd degree
      if (leadingCoef > 0) {
        steps.push(`  • As ${variable}→-∞: f(${variable})→-∞`);
        steps.push(`  • As ${variable}→+∞: f(${variable})→+∞`);
      } else {
        steps.push(`  • As ${variable}→-∞: f(${variable})→+∞`);
        steps.push(`  • As ${variable}→+∞: f(${variable})→-∞`);
      }
    } else {
      // Even degree
      if (leadingCoef > 0) {
        steps.push(`  • As ${variable}→±∞: f(${variable})→+∞`);
      } else {
        steps.push(`  • As ${variable}→±∞: f(${variable})→-∞`);
      }
    }
  }
  
  // Symmetry
  steps.push(`\nSymmetry Analysis:`);
  let symmetry = 'none';
  
  // Check if all powers are even (y-axis symmetry)
  const allEven = terms.every(term => !term.variable || term.power % 2 === 0);
  // Check if all powers are odd (origin symmetry)
  const allOdd = terms.every(term => !term.variable || term.power % 2 === 1);
  
  if (allEven) {
    symmetry = 'even';
    steps.push(`  • Even function: f(-${variable}) = f(${variable}) (symmetric about y-axis)`);
  } else if (allOdd) {
    symmetry = 'odd';
    steps.push(`  • Odd function: f(-${variable}) = -f(${variable}) (symmetric about origin)`);
  } else {
    steps.push(`  • Neither even nor odd (no symmetry)`);
  }
  
  // Generate plot points
  const plotPoints = [];
  const step = (domain.max - domain.min) / 100;
  for (let x = domain.min; x <= domain.max; x += step) {
    let y = 0;
    terms.forEach(term => {
      if (term.variable === null || term.power === 0) {
        y += term.coef;
      } else {
        y += term.coef * Math.pow(x, term.power);
      }
    });
    plotPoints.push({ x, y });
  }
  
  return {
    expression: current,
    variable,
    functionType,
    degree,
    leadingCoef,
    domain: analyzedDomain,
    range: analyzedRange,
    intercepts,
    criticalPoints,
    symmetry,
    steps,
    plotPoints,
    terms
  };
};

// Example functions
const exampleFunctions = [
  { expr: "x^2", desc: "Simple parabola" },
  { expr: "x^3 - 3x", desc: "Cubic with local extrema" },
  { expr: "2x + 1", desc: "Linear function" },
  { expr: "-x^2 + 4x - 3", desc: "Downward parabola" },
  { expr: "x^4 - 4x^2", desc: "Quartic function" },
  { expr: "x^3 + 2x^2 - x + 1", desc: "General cubic" },
];

// Copy Button Component
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text?.toString() || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopy} title={`Copy ${label}`}>
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Check className="w-3.5 h-3.5 text-green-500" />
          </motion.div>
        ) : (
          <motion.div key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Copy className="w-3.5 h-3.5" />
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
};

// Simple Graph Visualization Component
const FunctionGraph = ({ result, showGrid = true }) => {
  if (!result || !result.plotPoints || result.plotPoints.length === 0) {
    return (
      <div className="w-full h-[400px] flex items-center justify-center bg-muted/20 rounded-xl border-2 border-dashed">
        <p className="text-muted-foreground">Enter a function to see the graph</p>
      </div>
    );
  }
  
  const points = result.plotPoints;
  const xValues = points.map(p => p.x);
  const yValues = points.map(p => p.y).filter(y => isFinite(y));
  
  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);
  const yMin = Math.min(...yValues, -10);
  const yMax = Math.max(...yValues, 10);
  
  const width = 800;
  const height = 400;
  const padding = 50;
  
  const xScale = (x) => padding + ((x - xMin) / (xMax - xMin)) * (width - 2 * padding);
  const yScale = (y) => height - padding - ((y - yMin) / (yMax - yMin)) * (height - 2 * padding);
  
  // Generate path for the function
  const pathData = points
    .filter(p => isFinite(p.y) && p.y >= yMin - 1 && p.y <= yMax + 1)
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.x)},${yScale(p.y)}`)
    .join(' ');
  
  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto max-w-[800px] mx-auto">
        <defs>
          <linearGradient id="graphGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="hsl(262, 83%, 58%)" stopOpacity="0.2" />
            <stop offset="100%" stopColor="hsl(262, 83%, 58%)" stopOpacity="0" />
          </linearGradient>
        </defs>
        
        {/* Grid */}
        {showGrid && (
          <g stroke="hsl(var(--border))" strokeWidth="0.5" opacity="0.3">
            {Array.from({ length: 11 }, (_, i) => {
              const x = padding + (i / 10) * (width - 2 * padding);
              return <line key={`vgrid-${i}`} x1={x} y1={padding} x2={x} y2={height - padding} />;
            })}
            {Array.from({ length: 11 }, (_, i) => {
              const y = padding + (i / 10) * (height - 2 * padding);
              return <line key={`hgrid-${i}`} x1={padding} y1={y} x2={width - padding} y2={y} />;
            })}
          </g>
        )}
        
        {/* Axes */}
        <g stroke="hsl(var(--foreground))" strokeWidth="2">
          {/* X-axis */}
          {yMin <= 0 && yMax >= 0 && (
            <line 
              x1={padding} 
              y1={yScale(0)} 
              x2={width - padding} 
              y2={yScale(0)}
              strokeWidth="2"
            />
          )}
          {/* Y-axis */}
          {xMin <= 0 && xMax >= 0 && (
            <line 
              x1={xScale(0)} 
              y1={padding} 
              x2={xScale(0)} 
              y2={height - padding}
              strokeWidth="2"
            />
          )}
        </g>
        
        {/* Function curve */}
        <path
          d={pathData}
          fill="none"
          stroke="hsl(262, 83%, 58%)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Intercepts */}
        {result.intercepts.x.map((xInt, idx) => {
          if (xInt >= xMin && xInt <= xMax) {
            return (
              <circle
                key={`x-int-${idx}`}
                cx={xScale(xInt)}
                cy={yScale(0)}
                r="5"
                fill="hsl(142, 76%, 36%)"
                stroke="white"
                strokeWidth="2"
              />
            );
          }
          return null;
        })}
        
        {result.intercepts.y !== null && (
          <circle
            cx={xScale(0)}
            cy={yScale(result.intercepts.y)}
            r="5"
            fill="hsl(221, 83%, 53%)"
            stroke="white"
            strokeWidth="2"
          />
        )}
        
        {/* Critical points */}
        {result.criticalPoints.map((cp, idx) => {
          if (cp.x >= xMin && cp.x <= xMax && cp.y >= yMin && cp.y <= yMax) {
            return (
              <circle
                key={`crit-${idx}`}
                cx={xScale(cp.x)}
                cy={yScale(cp.y)}
                r="6"
                fill="hsl(0, 84%, 60%)"
                stroke="white"
                strokeWidth="2"
              />
            );
          }
          return null;
        })}
        
        {/* Axis labels */}
        <text x={width - padding + 10} y={yScale(0) + 5} fontSize="14" fill="hsl(var(--foreground))">
          {result.variable}
        </text>
        <text x={xScale(0) + 5} y={padding - 10} fontSize="14" fill="hsl(var(--foreground))">
          y
        </text>
      </svg>
    </div>
  );
};

function FunctionGrapher() {
  const [expression, setExpression] = useState("x^2");
  const [variable, setVariable] = useState("x");
  const [domainMin, setDomainMin] = useState("-10");
  const [domainMax, setDomainMax] = useState("10");
  const [showGrid, setShowGrid] = useState(true);
  const [showSteps, setShowSteps] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  
  const result = useMemo(() => {
    try {
      const domain = {
        min: parseFloat(domainMin) || -10,
        max: parseFloat(domainMax) || 10
      };
      return analyzeFunction(expression, variable, domain);
    } catch (e) {
      return null;
    }
  }, [expression, variable, domainMin, domainMax]);
  
  const handleReset = useCallback(() => {
    setExpression("x^2");
    setVariable("x");
    setDomainMin("-10");
    setDomainMax("10");
  }, []);
  
  const loadExample = (expr) => {
    setExpression(expr);
  };
  
  const handleExport = () => {
    if (!result) return;
    const data = {
      expression: result.expression,
      variable: result.variable,
      functionType: result.functionType,
      domain: result.domain,
      range: result.range,
      intercepts: result.intercepts,
      criticalPoints: result.criticalPoints,
      steps: result.steps,
      timestamp: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `function-analysis-${Date.now()}.json`;
    a.click();
  };
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <Helmet>
        <title>Function Grapher & Analyzer - Domain, Range, Intercepts & More</title>
        <meta name="description" content="Analyze and graph functions. Find domain, range, intercepts, critical points, and inflection points with detailed explanations." />
      </Helmet>
      
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
              <LineChart className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Function Grapher & Analyzer</h1>
              <p className="text-muted-foreground mt-1">Visualize and analyze function properties</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} disabled={!result} className="gap-2">
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          </div>
        </div>
        
        {/* Quick Stats Bar */}
        {result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-3"
          >
            <Card className="p-3 bg-indigo-500/5 border-indigo-500/20">
              <div className="text-xs text-muted-foreground mb-1">Type</div>
              <div className="text-lg font-bold text-indigo-600 capitalize">{result.functionType}</div>
            </Card>
            <Card className="p-3 bg-purple-500/5 border-purple-500/20">
              <div className="text-xs text-muted-foreground mb-1">Degree</div>
              <div className="text-lg font-bold text-purple-600">{result.degree}</div>
            </Card>
            <Card className="p-3 bg-violet-500/5 border-violet-500/20">
              <div className="text-xs text-muted-foreground mb-1">X-Intercepts</div>
              <div className="text-lg font-bold text-violet-600">{result.intercepts.x.length}</div>
            </Card>
            <Card className="p-3 bg-fuchsia-500/5 border-fuchsia-500/20">
              <div className="text-xs text-muted-foreground mb-1">Critical Points</div>
              <div className="text-lg font-bold text-fuchsia-600">{result.criticalPoints.length}</div>
            </Card>
          </motion.div>
        )}
      </motion.div>
      
      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto">
          <TabsTrigger value="calculator" className="py-3">Analyzer</TabsTrigger>
          <TabsTrigger value="properties" className="py-3">Properties</TabsTrigger>
          <TabsTrigger value="examples" className="py-3">Examples</TabsTrigger>
          <TabsTrigger value="guide" className="py-3">Complete Guide</TabsTrigger>
        </TabsList>
        
        {/* Calculator Tab */}
        <TabsContent value="calculator" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Input Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:sticky lg:top-24 lg:self-start space-y-6"
            >
              <Card className="rounded-2xl p-6 border-2 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-indigo-600" />
                    <span className="font-semibold text-foreground text-lg">Input Function</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-9 w-9 hover:bg-destructive/10 hover:text-destructive" 
                    onClick={handleReset}
                    title="Reset All"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm mb-2 block font-medium">Function f({variable})</Label>
                    <Input
                      type="text"
                      value={expression}
                      onChange={(e) => setExpression(e.target.value)}
                      className="h-12 text-lg font-mono"
                      placeholder="e.g., x^2"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Use ^ for powers (e.g., x^2, x^3)
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-sm mb-2 block font-medium">Variable</Label>
                    <Input
                      type="text"
                      value={variable}
                      onChange={(e) => setVariable(e.target.value)}
                      className="h-12 text-lg font-mono"
                      maxLength={1}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm mb-3 block font-medium">Domain Range</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Min</Label>
                        <Input
                          type="number"
                          value={domainMin}
                          onChange={(e) => setDomainMin(e.target.value)}
                          className="h-10 text-sm font-mono"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Max</Label>
                        <Input
                          type="number"
                          value={domainMax}
                          onChange={(e) => setDomainMax(e.target.value)}
                          className="h-10 text-sm font-mono"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Set viewing window for graph
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <Label className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <Grid3x3 className="w-4 h-4" />
                      Show Grid
                    </Label>
                    <button
                      onClick={() => setShowGrid(!showGrid)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        showGrid ? 'bg-indigo-600' : 'bg-muted'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                          showGrid ? 'translate-x-5' : ''
                        }`}
                      />
                    </button>
                  </div>
                </div>
                
                {/* Validation Status */}
                <div className="mt-6">
                  {result ? (
                    <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-600">Valid Expression ✓</span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                      <XCircle className="w-5 h-5 text-destructive mt-0.5" />
                      <div>
                        <span className="text-sm font-medium text-destructive block">Invalid Expression</span>
                        <span className="text-xs text-destructive/80">Check your function syntax</span>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
              
              {/* Quick Examples */}
              <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-indigo-500/5 to-purple-500/5">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  Quick Examples
                </h3>
                <div className="space-y-2">
                  {exampleFunctions.map((ex, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      className="w-full justify-start text-left h-auto py-3"
                      onClick={() => loadExample(ex.expr)}
                    >
                      <div className="flex-1">
                        <div className="font-mono text-sm">f({variable}) = {ex.expr}</div>
                        <div className="text-xs text-muted-foreground">{ex.desc}</div>
                      </div>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  ))}
                </div>
              </Card>
            </motion.div>
            
            {/* Center/Right Column - Graph & Results */}
            <div className="lg:col-span-2 space-y-6">
              {/* Graph Display */}
              {result && (
                <Card className="rounded-2xl p-6 border-2 shadow-lg">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="font-semibold text-foreground flex items-center gap-2 text-lg">
                      <LineChart className="w-5 h-5 text-indigo-600" />
                      Function Graph
                    </h2>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setShowGrid(!showGrid)} className="gap-2">
                        {showGrid ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        Grid
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-indigo-500/5 to-purple-500/5 rounded-xl p-4 border">
                    <FunctionGraph result={result} showGrid={showGrid} />
                  </div>
                  
                  {/* Legend */}
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 rounded-full bg-[hsl(262,83%,58%)]" />
                      <span>Function</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 rounded-full bg-green-600" />
                      <span>X-intercepts</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 rounded-full bg-blue-600" />
                      <span>Y-intercept</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 rounded-full bg-red-600" />
                      <span>Critical points</span>
                    </div>
                  </div>
                </Card>
              )}
              
              {/* Analysis Results */}
              {result && (
                <Card className="rounded-2xl p-6 border-2 shadow-lg">
                  <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    Function Analysis
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-indigo-500/5 rounded-lg border border-indigo-500/20">
                        <div className="text-xs text-muted-foreground mb-1">Function Type</div>
                        <div className="font-semibold text-lg capitalize">{result.functionType}</div>
                      </div>
                      <div className="p-4 bg-purple-500/5 rounded-lg border border-purple-500/20">
                        <div className="text-xs text-muted-foreground mb-1">Degree</div>
                        <div className="font-semibold text-lg">{result.degree}</div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-muted/30 rounded-lg border">
                      <div className="text-sm font-semibold mb-2">Domain</div>
                      <div className="font-mono text-sm">{result.domain.description}</div>
                    </div>
                    
                    <div className="p-4 bg-muted/30 rounded-lg border">
                      <div className="text-sm font-semibold mb-2">Range</div>
                      <div className="font-mono text-sm">{result.range.description}</div>
                    </div>
                    
                    <div className="p-4 bg-muted/30 rounded-lg border">
                      <div className="text-sm font-semibold mb-2">Intercepts</div>
                      <div className="space-y-1 text-sm">
                        <div>Y-intercept: <span className="font-mono">({result.variable}=0, y={result.intercepts.y?.toFixed(4) || '0'})</span></div>
                        {result.intercepts.x.length > 0 ? (
                          <div>X-intercepts: {result.intercepts.x.map((x, i) => (
                            <span key={i} className="font-mono">({result.variable}={x.toFixed(4)}, y=0){i < result.intercepts.x.length - 1 ? ', ' : ''}</span>
                          ))}</div>
                        ) : (
                          <div>X-intercepts: None (or complex)</div>
                        )}
                      </div>
                    </div>
                    
                    {result.criticalPoints.length > 0 && (
                      <div className="p-4 bg-muted/30 rounded-lg border">
                        <div className="text-sm font-semibold mb-2">Critical Points</div>
                        <div className="space-y-1 text-sm">
                          {result.criticalPoints.map((cp, idx) => (
                            <div key={idx} className="font-mono">
                              ({cp.x.toFixed(4)}, {cp.y.toFixed(4)})
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="p-4 bg-muted/30 rounded-lg border">
                      <div className="text-sm font-semibold mb-2">Symmetry</div>
                      <div className="text-sm capitalize">{result.symmetry === 'none' ? 'No symmetry' : result.symmetry === 'even' ? 'Even function (y-axis symmetry)' : 'Odd function (origin symmetry)'}</div>
                    </div>
                  </div>
                </Card>
              )}
              
              {/* Step-by-Step Analysis */}
              {result && (
                <Card className="rounded-2xl p-6 border-2 shadow-lg">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="font-semibold text-foreground flex items-center gap-2 text-lg">
                      <BookOpen className="w-5 h-5 text-indigo-600" />
                      Step-by-Step Analysis
                      <Sparkles className="w-4 h-4 text-yellow-500 ml-auto" />
                    </h2>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSteps(!showSteps)}
                      className="gap-2"
                    >
                      {showSteps ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      <span className="hidden sm:inline">{showSteps ? 'Hide' : 'Show'}</span>
                    </Button>
                  </div>
                  
                  <AnimatePresence>
                    {showSteps && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-3"
                      >
                        {result.steps.map((step, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.02 }}
                            className="flex gap-3 p-3 bg-muted/30 rounded-lg border"
                          >
                            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-foreground font-mono whitespace-pre-line">{step}</p>
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>
        
        {/* Properties Tab */}
        <TabsContent value="properties" className="space-y-6">
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Info className="w-6 h-6 text-indigo-600" />
              Function Properties Reference
            </h2>
            
            <div className="space-y-6">
              {/* Domain */}
              <div className="p-5 bg-gradient-to-br from-indigo-500/5 to-blue-500/5 rounded-xl border">
                <h3 className="font-bold text-lg mb-3 text-indigo-600">Domain</h3>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  The domain is the set of all possible input values (x-values) for which the function is defined.
                </p>
                <div className="space-y-2">
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm">Polynomials:</strong>
                    <p className="text-xs text-muted-foreground mt-1">Domain is all real numbers (-∞, ∞)</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm">Rational Functions:</strong>
                    <p className="text-xs text-muted-foreground mt-1">Exclude values that make denominator zero</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm">Square Root Functions:</strong>
                    <p className="text-xs text-muted-foreground mt-1">Expression under root must be ≥ 0</p>
                  </div>
                </div>
              </div>
              
              {/* Range */}
              <div className="p-5 bg-gradient-to-br from-purple-500/5 to-pink-500/5 rounded-xl border">
                <h3 className="font-bold text-lg mb-3 text-purple-600">Range</h3>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  The range is the set of all possible output values (y-values) that the function can produce.
                </p>
                <div className="space-y-2">
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm">Linear (y = mx + b):</strong>
                    <p className="text-xs text-muted-foreground mt-1">Range is all real numbers</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm">Quadratic (y = ax² + bx + c):</strong>
                    <p className="text-xs text-muted-foreground mt-1">If a {`>`} 0: [vertex_y, ∞); If a {`<`} 0: (-∞, vertex_y]</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm">Odd Degree Polynomial:</strong>
                    <p className="text-xs text-muted-foreground mt-1">Range is all real numbers</p>
                  </div>
                </div>
              </div>
              
              {/* Intercepts */}
              <div className="p-5 bg-gradient-to-br from-green-500/5 to-emerald-500/5 rounded-xl border">
                <h3 className="font-bold text-lg mb-3 text-green-600">Intercepts</h3>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  Points where the graph crosses or touches the coordinate axes.
                </p>
                <div className="space-y-3">
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm text-blue-600">Y-Intercept:</strong>
                    <p className="text-xs text-muted-foreground mt-1">
                      Find by setting x = 0. Every function has at most one y-intercept (unless vertical line).
                    </p>
                    <code className="text-xs font-mono block mt-2 p-2 bg-background rounded">y-intercept = f(0)</code>
                  </div>
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm text-green-600">X-Intercepts (Roots/Zeros):</strong>
                    <p className="text-xs text-muted-foreground mt-1">
                      Find by solving f(x) = 0. A function can have multiple x-intercepts.
                    </p>
                    <code className="text-xs font-mono block mt-2 p-2 bg-background rounded">Solve: f(x) = 0</code>
                  </div>
                </div>
              </div>
              
              {/* Critical Points */}
              <div className="p-5 bg-gradient-to-br from-orange-500/5 to-red-500/5 rounded-xl border">
                <h3 className="font-bold text-lg mb-3 text-orange-600">Critical Points</h3>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  Points where the derivative is zero or undefined. These are potential locations for local maxima and minima.
                </p>
                <div className="space-y-2">
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm">Finding Critical Points:</strong>
                    <ol className="text-xs text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                      <li>Find f'(x)</li>
                      <li>Set f'(x) = 0 and solve for x</li>
                      <li>Check where f'(x) is undefined</li>
                      <li>Evaluate f(x) at each critical x to get y-coordinate</li>
                    </ol>
                  </div>
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm">Classification (Second Derivative Test):</strong>
                    <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                      <li>If f{`''`}(x) {`>`} 0: Local minimum</li>
                      <li>If f{`''`}(x) {`<`} 0: Local maximum</li>
                      <li>If f{`''`}(x) = 0: Test is inconclusive (use first derivative test)</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Inflection Points */}
              <div className="p-5 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 rounded-xl border">
                <h3 className="font-bold text-lg mb-3 text-cyan-600">Inflection Points</h3>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  Points where the concavity of the function changes (from concave up to concave down or vice versa).
                </p>
                <div className="p-3 bg-background/60 rounded">
                  <strong className="text-sm">Finding Inflection Points:</strong>
                  <ol className="text-xs text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                    <li>Find f''(x)</li>
                    <li>Set f''(x) = 0 and solve for x</li>
                    <li>Verify concavity changes by checking sign of f''(x) on either side</li>
                    <li>Evaluate f(x) at inflection x to get y-coordinate</li>
                  </ol>
                </div>
              </div>
              
              {/* Symmetry */}
              <div className="p-5 bg-gradient-to-br from-violet-500/5 to-purple-500/5 rounded-xl border">
                <h3 className="font-bold text-lg mb-3 text-violet-600">Symmetry</h3>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  Functions can have different types of symmetry that simplify analysis and graphing.
                </p>
                <div className="space-y-2">
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm">Even Function (Y-axis symmetry):</strong>
                    <p className="text-xs text-muted-foreground mt-1">f(-x) = f(x) for all x in domain</p>
                    <p className="text-xs text-muted-foreground">Examples: x², x⁴, cos(x)</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded">
                    <strong className="text-sm">Odd Function (Origin symmetry):</strong>
                    <p className="text-xs text-muted-foreground mt-1">f(-x) = -f(x) for all x in domain</p>
                    <p className="text-xs text-muted-foreground">Examples: x, x³, sin(x)</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
        
        {/* Examples Tab */}
        <TabsContent value="examples" className="space-y-6">
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              Worked Examples
            </h2>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="ex-1">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 1: Simple Parabola f(x) = x²</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Function:</strong> f(x) = x²
                    </div>
                    <div>
                      <strong>Analysis:</strong>
                      <ul className="list-disc list-inside space-y-2 mt-2 ml-2 text-sm">
                        <li><strong>Type:</strong> Quadratic (degree 2)</li>
                        <li><strong>Domain:</strong> All real numbers (-∞, ∞)</li>
                        <li><strong>Range:</strong> [0, ∞) - parabola opens upward</li>
                        <li><strong>Y-intercept:</strong> (0, 0)</li>
                        <li><strong>X-intercept:</strong> (0, 0) - single root at origin</li>
                        <li><strong>Critical point:</strong> (0, 0) - vertex is a minimum</li>
                        <li><strong>Symmetry:</strong> Even function (symmetric about y-axis)</li>
                        <li><strong>End behavior:</strong> As x→±∞, f(x)→∞</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-2">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 2: Cubic f(x) = x³ - 3x</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Function:</strong> f(x) = x³ - 3x
                    </div>
                    <div>
                      <strong>Analysis:</strong>
                      <ul className="list-disc list-inside space-y-2 mt-2 ml-2 text-sm">
                        <li><strong>Type:</strong> Cubic (degree 3)</li>
                        <li><strong>Domain:</strong> All real numbers</li>
                        <li><strong>Range:</strong> All real numbers</li>
                        <li><strong>Y-intercept:</strong> (0, 0)</li>
                        <li><strong>X-intercepts:</strong> x = 0, x = ±√3</li>
                        <li><strong>Critical points:</strong> f'(x) = 3x² - 3 = 0 → x = ±1</li>
                        <li>  • At x = -1: f(-1) = 2 (local maximum)</li>
                        <li>  • At x = 1: f(1) = -2 (local minimum)</li>
                        <li><strong>Inflection point:</strong> f''(x) = 6x = 0 → x = 0, point (0, 0)</li>
                        <li><strong>Symmetry:</strong> Odd function (symmetric about origin)</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-3">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 3: Downward Parabola f(x) = -x² + 4x - 3</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Function:</strong> f(x) = -x² + 4x - 3
                    </div>
                    <div>
                      <strong>Analysis:</strong>
                      <ul className="list-disc list-inside space-y-2 mt-2 ml-2 text-sm">
                        <li><strong>Type:</strong> Quadratic (degree 2)</li>
                        <li><strong>Leading coefficient:</strong> a = -1 (opens downward)</li>
                        <li><strong>Vertex:</strong> x = -b/(2a) = -4/(2(-1)) = 2</li>
                        <li>  • f(2) = -4 + 8 - 3 = 1</li>
                        <li>  • Vertex: (2, 1) - this is the maximum point</li>
                        <li><strong>Y-intercept:</strong> f(0) = -3, point (0, -3)</li>
                        <li><strong>X-intercepts:</strong> Using quadratic formula:</li>
                        <li>  • Discriminant: b² - 4ac = 16 - 12 = 4</li>
                        <li>  • x = (4 ± 2)/(-2) = x = 1 or x = 3</li>
                        <li><strong>Range:</strong> (-∞, 1] - maximum value is 1</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-4">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 4: Quartic f(x) = x⁴ - 4x²</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Function:</strong> f(x) = x⁴ - 4x²
                    </div>
                    <div>
                      <strong>Analysis:</strong>
                      <ul className="list-disc list-inside space-y-2 mt-2 ml-2 text-sm">
                        <li><strong>Type:</strong> Quartic (degree 4)</li>
                        <li><strong>Factored form:</strong> x²(x² - 4) = x²(x-2)(x+2)</li>
                        <li><strong>X-intercepts:</strong> x = 0, x = ±2</li>
                        <li><strong>Y-intercept:</strong> (0, 0)</li>
                        <li><strong>Critical points:</strong> f'(x) = 4x³ - 8x = 4x(x² - 2) = 0</li>
                        <li>  • x = 0: f(0) = 0</li>
                        <li>  • x = ±√2: f(±√2) = -4</li>
                        <li><strong>Second derivative test:</strong></li>
                        <li>  {String.fromCharCode(8226)} f&apos;&apos;(0) = -8 {String.fromCharCode(60)} 0 {String.fromCharCode(8594)} local maximum at (0, 0)</li>
                        <li>  • f&apos;&apos;(±√2) = 16 {String.fromCharCode(62)} 0 → local minima at (±√2, -4)</li>
                        <li><strong>Symmetry:</strong> Even function (all powers are even)</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
          
          {/* Practice Problems */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-orange-500/5 to-red-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Hash className="w-5 h-5 text-orange-600" />
              Practice Problems
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "f(x) = 2x + 3",
                "f(x) = x² - 4",
                "f(x) = x³ + x²",
                "f(x) = -3x² + 6x - 2",
                "f(x) = x⁴ - x²",
                "f(x) = x³ - 6x² + 9x",
                "f(x) = 2x³ - 3x² - 12x + 5",
                "f(x) = x⁴ - 5x² + 4"
              ].map((problem, idx) => (
                <div key={idx} className="p-4 bg-background/60 rounded-lg border hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="font-mono text-sm">{problem}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 ml-11">
                    Find: domain, range, intercepts, critical points
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
        
        {/* Complete Guide Tab */}
        <TabsContent value="guide" className="space-y-6">
          {/* FAQ Section */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="faq-1">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">How do I determine if a function is continuous?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      A function f is continuous at a point x=a if three conditions are met:
                    </p>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                      <li>f(a) exists (the function is defined at a)</li>
                      <li>lim (x→a) f(x) exists (the limit exists as x approaches a)</li>
                      <li>lim (x→a) f(x) = f(a) (the limit equals the function value)</li>
                    </ol>
                    <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20 mt-3">
                      <strong className="text-blue-600">Common Continuous Functions:</strong>
                      <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
                        <li>All polynomials are continuous everywhere</li>
                        <li>Rational functions are continuous on their domain</li>
                        <li>Exponential and logarithmic functions are continuous on their domain</li>
                        <li>Trigonometric functions are continuous on their domain</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-2">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">What's the difference between local and global extrema?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <div className="p-4 bg-background/60 rounded-lg border">
                      <strong>Local Extrema (Relative):</strong>
                      <p className="text-sm text-muted-foreground mt-2">
                        A point is a local maximum if f(c) ≥ f(x) for all x in some open interval around c. 
                        Local minimum is similar but f(c) ≤ f(x). Found at critical points.
                      </p>
                    </div>
                    <div className="p-4 bg-background/60 rounded-lg border">
                      <strong>Global Extrema (Absolute):</strong>
                      <p className="text-sm text-muted-foreground mt-2">
                        A point is a global maximum if f(c) ≥ f(x) for all x in the entire domain. 
                        It's the highest point on the entire graph. Similarly for global minimum.
                      </p>
                    </div>
                    <div className="bg-purple-500/10 p-3 rounded border border-purple-500/20">
                      <strong className="text-sm">Key Point:</strong>
                      <p className="text-xs text-muted-foreground mt-1">
                        Global extrema are also local extrema, but local extrema aren't necessarily global. 
                        For polynomials of odd degree, there are no global extrema. For even degree with positive leading coefficient, 
                        the global minimum exists but no global maximum.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-3">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">How do I find the domain of a function?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      To find the domain, identify values of x that make the function undefined:
                    </p>
                    <div className="space-y-2">
                      <div className="p-3 bg-background/60 rounded border">
                        <strong className="text-sm">Division by Zero:</strong>
                        <p className="text-xs text-muted-foreground mt-1">
                          For f(x) = p(x)/q(x), exclude values where q(x) = 0
                        </p>
                      </div>
                      <div className="p-3 bg-background/60 rounded border">
                        <strong className="text-sm">Even Roots:</strong>
                        <p className="text-xs text-muted-foreground mt-1">
                          For √(g(x)), require g(x) ≥ 0 (expression under root must be non-negative)
                        </p>
                      </div>
                      <div className="p-3 bg-background/60 rounded border">
                        <strong className="text-sm">Logarithms:</strong>
                        <p className="text-xs text-muted-foreground mt-1">
                          For log(g(x)), require g(x) {String.fromCharCode(62)} 0 (argument must be positive)
                        </p>
                      </div>
                      <div className="p-3 bg-background/60 rounded border">
                        <strong className="text-sm">Polynomials:</strong>
                        <p className="text-xs text-muted-foreground mt-1">
                          Domain is always all real numbers (-∞, ∞)
                        </p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-4">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">What is concavity and how do I determine it?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      Concavity describes how a function curves. It's determined by the second derivative:
                    </p>
                    <div className="space-y-3">
                      <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                        <strong className="text-green-600">Concave Up (∪ shape):</strong>
                        <p className="text-sm text-muted-foreground mt-2">
                          When f&apos;&apos;(x) {`>`} 0, the function curves upward like a cup. The tangent lines lie below the curve. 
                          This indicates the function is accelerating upward.
                        </p>
                      </div>
                      <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
                        <strong className="text-orange-600">Concave Down (∩ shape):</strong>
                        <p className="text-sm text-muted-foreground mt-2">
                          When f&apos;&apos;(x) {`<`} 0, the function curves downward like a cap. The tangent lines lie above the curve. 
                          This indicates the function is decelerating.
                        </p>
                      </div>
                    </div>
                    <div className="bg-blue-500/10 p-3 rounded border border-blue-500/20">
                      <strong className="text-sm">Connection to Critical Points:</strong>
                      <p className="text-xs text-muted-foreground mt-1">
                        If f&apos;(c) = 0 and f&apos;&apos;(c) {`>`} 0, then x = c is a local minimum (concave up). 
                        If f&apos;(c) = 0 and f&apos;&apos;(c) {`<`} 0, then x = c is a local maximum (concave down).
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-5">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">What are the practical applications of function analysis?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="p-3 bg-background/60 rounded border">
                        <strong className="text-sm">Optimization Problems:</strong>
                        <p className="text-xs text-muted-foreground mt-1">
                          Finding maximum profit, minimum cost, optimal dimensions for manufacturing
                        </p>
                      </div>
                      <div className="p-3 bg-background/60 rounded border">
                        <strong className="text-sm">Physics & Motion:</strong>
                        <p className="text-xs text-muted-foreground mt-1">
                          Analyzing position, velocity, acceleration; finding maximum height of projectiles
                        </p>
                      </div>
                      <div className="p-3 bg-background/60 rounded border">
                        <strong className="text-sm">Economics:</strong>
                        <p className="text-xs text-muted-foreground mt-1">
                          Marginal cost/revenue analysis, elasticity of demand, profit maximization
                        </p>
                      </div>
                      <div className="p-3 bg-background/60 rounded border">
                        <strong className="text-sm">Biology:</strong>
                        <p className="text-xs text-muted-foreground mt-1">
                          Population growth models, drug concentration optimization, epidemic spread
                        </p>
                      </div>
                      <div className="p-3 bg-background/60 rounded border">
                        <strong className="text-sm">Engineering:</strong>
                        <p className="text-xs text-muted-foreground mt-1">
                          Structural design, signal processing, control systems, material strength
                        </p>
                      </div>
                      <div className="p-3 bg-background/60 rounded border">
                        <strong className="text-sm">Computer Graphics:</strong>
                        <p className="text-xs text-muted-foreground mt-1">
                          Curve design, animation paths, rendering algorithms
                        </p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
          
          {/* Graphing Techniques */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-indigo-500/5 to-purple-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <LineChart className="w-5 h-5 text-indigo-600" />
              Complete Graphing Technique
            </h2>
            
            <div className="space-y-4">
              <div className="p-5 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-3">Step-by-Step Graphing Process</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">1</div>
                    <div>
                      <strong>Determine Domain & Range:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Identify restrictions and possible output values</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">2</div>
                    <div>
                      <strong>Find Intercepts:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Y-intercept at x=0, X-intercepts by solving f(x)=0</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">3</div>
                    <div>
                      <strong>Check Symmetry:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Even (y-axis), odd (origin), or neither</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">4</div>
                    <div>
                      <strong>Find Critical Points:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Solve f'(x)=0 to locate maxima and minima</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">5</div>
                    <div>
                      <strong>Determine Concavity:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Use f''(x) to find concave up/down regions and inflection points</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">6</div>
                    <div>
                      <strong>Analyze End Behavior:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Determine what happens as x→±∞</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">7</div>
                    <div>
                      <strong>Plot Key Points & Sketch:</strong>
                      <p className="text-xs text-muted-foreground mt-1">Use all gathered information to draw smooth curve</p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default FunctionGrapher;
